import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHub, usePrivate } from '../context';
import { ARMOR_BUCKETS, equipItem, inBucket, isMasterwork, loadGear, loadStash, SUBCLASS_BUCKET, WEAPON_BUCKETS, wornFromItem, type GearChar, type Stash, type StashItem, type WornItem } from '../character';
import { buildScene, warmGear3d, type Scene } from '../gear3d';
import { BungieError, img, sameOriginImg } from '../../lib/bungie';
import { Art, Draw, Pic } from '../ui';

/**
 * Port of the app's 3D view (GuardianPage): the in-game character screen. The Guardian, built from Bungie's 3D gear data,
 * fills the page on a dais lit in the subclass colour; over it float the class and subclass, the characters, the
 * subclass diamond and weapons on the left, Power, stats and armour on the right, ghost / sparrow / ship small, a caption
 * under the Guardian and the scene's controls at the foot. A gear square under the pointer grows, names itself under the
 * Guardian and brings the camera to that part of the body; a click opens the other items its slot carries (up to nine)
 * and a click on one of those equips it, as in the game.
 */

const TIER_COLORS: Record<number, string> = { 6: '#CEAE33', 5: '#7A4FB0', 4: '#5076A3', 3: '#366F42', 2: '#C3BCB4' };
const ELEMENTS: [RegExp, string, string][] = [
  [/prismatic/i, 'Prismatic', '#C25AC9'], [/arcstrider|striker|stormcaller|\barc\b/i, 'Arc', '#3F9FD8'], [/gunslinger|sunbreaker|dawnblade|solar/i, 'Solar', '#E8792B'],
  [/nightstalker|sentinel|voidwalker|void/i, 'Void', '#8B5FB5'], [/revenant|behemoth|shadebinder|stasis/i, 'Stasis', '#4D7FE0'], [/threadrunner|berserker|broodweaver|strand/i, 'Strand', '#2FB463'],
];
const EXTRA_BUCKETS = [4023194814, 2025709351, 284967655];   // ghost, sparrow, ship
const REGIONS = ['head', 'arms', 'chest', 'legs', 'class'];
const VIEWS: [string, string][] = [['final', 'Final PBR'], ['nodetail', 'Final (no detail)'], ['base', 'Base colour'], ['normal', 'Normal'], ['rough', 'Roughness'], ['metal', 'Metallic'],
  ['spec', 'Specular'], ['emissive', 'Emissive'], ['mask', 'Material mask'], ['shader', 'Shader colour']];
const PLACE_KEY = 'd2c.3d.place';
const FOCUS_WAIT = 180;   // ms a gear square must be under the pointer before the camera goes to that piece
const scenes = new Map<string, Scene>();   // built once per character and outfit while the page is open

const dark = (hex: string, k: number) => {
  const n = parseInt(hex.slice(1), 16), c = (v: number, add: number) => Math.round(v * k + add);
  return `rgb(${c(n >> 16, 7)}, ${c((n >> 8) & 255, 8)}, ${c(n & 255, 11)})`;
};
// why Bungie said no to an equip, in words that say what to do about it
const whyNot = (e: unknown) => {
  const st = e instanceof BungieError ? e.status : '';
  const msg = e instanceof Error ? e.message : String(e);
  if (/ApplicationScope/i.test(st + msg)) return "This site's Bungie sign-in is not allowed to move or equip gear. Sign out and sign in again; if it keeps saying this, the site's Bungie app needs the \"Move or equip Destiny gear\" permission.";
  if (/UniqueEquip|Exotic/i.test(st + msg)) return 'You can only wear one exotic armour piece (and one exotic weapon) at a time: take the other exotic off first.';
  if (/Location|Activity|NotInTower|CannotPerform/i.test(st)) return 'Bungie only lets gear change while you are in orbit, in a social space or offline, not in an activity.';
  if (/SignedOut|run out|Unauthorized|AuthorizationCode|WebAuth/i.test(st + msg)) return 'Your Bungie sign-in has run out. Sign in again.';
  if (/Throttle|TooFast/i.test(st)) return 'Bungie asked to slow down: wait a second and try again.';
  return `Bungie said: ${msg}${st ? ` (${st})` : ''}`;
};

const store = { get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* storage blocked */ } } };

export default function Guardian3D() {
  const h = useHub();
  const nav = useNavigate();
  const { data: gear, error } = usePrivate<GearChar[]>('gear', (t) => loadGear(h.player!, t || undefined), { public: true });
  const { data: stash } = usePrivate<Stash>('stash', (t) => loadStash(h.player!, t));
  const char = h.char;
  const me = gear?.find((c) => c.id === char?.id);
  // items equipped here before Bungie's profile shows them (it can lag half a minute): drawn at once, dropped once it does
  const [worn, setWorn] = useState<Record<number, WornItem>>({});
  useEffect(() => { setWorn((w) => Object.fromEntries(Object.entries(w).filter(([b, it]) => !me?.items.some((x) => x.bucket === Number(b) && x.hash === it.hash)))); }, [gear]);   // eslint-disable-line react-hooks/exhaustive-deps
  const meItems = me ? me.items.map((i) => worn[i.bucket] ?? i) : [];
  const [scene, setScene] = useState<Scene>();
  const [shownKey, setShownKey] = useState('');
  const [centre, setCentreText] = useState('');
  const [info, setInfo] = useState('');
  const [status, setStatus] = useState('');
  const [hover, setHover] = useState<WornItem>();
  const [open, setOpen] = useState<{ item: WornItem; armour: boolean; at: DOMRect }>();
  const [subOpen, setSubOpen] = useState<DOMRect>();
  const [subHover, setSubHover] = useState<WornItem['plugs'][number]>();
  const [more, setMore] = useState(false);
  const [view, setView] = useState(0);
  const [busy, setBusy] = useState(false);
  const [calm, setCalm] = useState(false);   // phones: the gear and buttons tucked away while the Guardian is turned, back on a tap
  const frame = useRef<HTMLIFrameElement>(null);
  const left = useRef<HTMLDivElement>(null), right = useRef<HTMLDivElement>(null), stage = useRef<HTMLDivElement>(null), body = useRef<HTMLDivElement>(null);
  const [k, setK] = useState(1);   // each side shrinks to fit a short window instead of scrolling, as the app's do

  const sub = me?.items.find((i) => i.bucket === SUBCLASS_BUCKET);
  const [, , colour] = ELEMENTS.find(([rx]) => rx.test(`${sub?.name ?? ''} ${sub?.type ?? ''}`)) ?? [null, 'Solar', '#E8792B'];
  const armour = me ? ARMOR_BUCKETS.map((b) => meItems.find((i) => i.bucket === b)).filter((i): i is WornItem => !!i) : [];
  const key = me ? `${me.id}:${armour.map((a) => `${a.hash}.${a.shader ?? 0}.${a.ornament ?? 0}`).join(',')}` : '';

  // places to stand the Guardian in: the artwork of what is on in the game now (as the app does), then none
  const places = [...h.ops, ...h.cards.flatMap((c) => c.pages)].filter((o) => o.image).map((o) => ({ name: o.name, url: sameOriginImg(o.image)! }))
    .filter((p, i, all) => all.findIndex((x) => x.url === p.url) === i);
  const [place, setPlace] = useState(-1);
  const placeAt = place < 0 ? Math.max(0, places.findIndex((p) => p.name === store.get(PLACE_KEY))) : place;
  const placeNow = placeAt < places.length && store.get(PLACE_KEY) !== '' ? places[placeAt] : undefined;

  const run = (fn: string, ...args: unknown[]) => { try { (frame.current?.contentWindow as any)?.[fn]?.(...args); } catch { /* not loaded yet */ } };
  // the camera goes to a piece only once the pointer rests on its square a moment: passing over the squares on the way
  // somewhere else no longer swings it from piece to piece
  const focusWait = useRef<number>(undefined);
  useEffect(() => () => window.clearTimeout(focusWait.current), []);

  // the gear database opens while the profile is still on its way
  useEffect(warmGear3d, []);
  // the 3D scene: from memory if built this visit; a new outfit is built out of sight and swapped in when ready
  useEffect(() => {
    if (!me || !char) return;
    if (!armour.length) { setCentreText('No armour equipped on this character.'); return; }
    const kept = scenes.get(key);
    if (kept) { setScene(kept); setShownKey(key); setCentreText(''); return; }
    let live = true;
    const quiet = shownKey.startsWith(`${me.id}:`);   // same character, new gear: keep the old one on screen meanwhile
    if (!quiet) { setScene(undefined); setInfo(''); }
    const pieces = armour.map((a) => ({ hash: a.hash, name: a.name, bucket: a.bucket, shader: a.shader, shaderName: a.shaderName, ornament: a.ornament, ornamentName: a.ornamentName }));
    buildScene(pieces, char.female, (s) => { if (live && !quiet) setCentreText(s); })
      .then((sc) => { scenes.set(key, sc); if (live) { setScene(sc); setShownKey(key); setCentreText(''); } })
      .catch((e) => { if (live) setCentreText(`The 3D model could not be built.\n${e?.message ?? e}`); });
    return () => { live = false; };
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (scene) (window as any).__d2cScene = scene.files;   // the viewer page (same site) reads the package from here
  // one viewer per character: a new outfit is handed to the running viewer, which builds it out of sight and swaps
  // it in (its reloadScene), instead of a new viewer and the "Assembling your Guardian" screen each time
  const frameKey = shownKey.split(':')[0];
  const frameScene = useRef('');
  useEffect(() => {
    if (!scene || !frameScene.current || frameScene.current === shownKey || !frameScene.current.startsWith(`${frameKey}:`)) return;
    frameScene.current = shownKey;
    run('reloadScene');
  }, [shownKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  // the Guardian stands in the middle of the free space between the two gear columns, not in the middle of the page
  const lineUp = () => {
    const f = frame.current?.getBoundingClientRect(), l = left.current?.getBoundingClientRect(), r = right.current?.getBoundingClientRect();
    if (!f || !l || !r || !f.width) return;
    run('setCentre', Math.round((l.right + r.left) / 2 - (f.left + f.width / 2)));
  };
  useLayoutEffect(() => {
    const ro = new ResizeObserver(() => {
      const bh = body.current?.clientHeight ?? 0;
      if (bh > 0) setK(Math.max(0.45, Math.min(1, bh / 700)));
      lineUp();
    });
    if (stage.current) ro.observe(stage.current);
    return () => ro.disconnect();
  });
  useEffect(() => {
    const on = (e: MessageEvent) => {
      const m = e.data?.d2c3d;
      if (typeof m !== 'string') return;
      if (m === 'turning') { if (matchMedia('(max-width: 600px)').matches) { setCalm(true); setOpen(undefined); setSubOpen(undefined); setMore(false); } }
      else if (m === 'tap') setCalm(false);
      else if (m.startsWith('ready:')) {
        lineUp();
        const [, parts, tris] = m.split(':');
        const orn = scene?.ornaments.length ? `ornaments: ${[...new Set(scene.ornaments)].join(', ')}` : 'no ornaments';
        setInfo(`3D (PBR): ${parts} parts, ${Number(tris || 0).toLocaleString()} triangles, ${orn}. Drag to turn, wheel to zoom.`);
      } else if (m.startsWith('error:')) setInfo(`3D: ${m.slice(6)}`);
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  });

  const equip = async (item: StashItem, _isArmour: boolean) => {
    if (busy || !char) return;
    setBusy(true); setOpen(undefined);
    setStatus(`Equipping ${item.name}...`);
    try {
      const t = await h.getToken();
      if (!t) throw new Error('Your Bungie sign-in has run out. Sign in again.');
      await equipItem(h.player!, t, char.id, item);
      // Bungie said yes: draw it now from the item alone (its Power, shader and ornament), rather than waiting until
      // Bungie's profile shows the swap (up to half a minute); the profile is read again quietly once it has caught up
      const slot = item.slot || item.bucket;
      const nowWorn = await wornFromItem(h.player!, t, item, slot).catch(() => undefined);
      if (nowWorn) setWorn((w) => ({ ...w, [slot]: nowWorn }));
      setStatus(`Equipped ${item.name}.`);
      window.setTimeout(() => h.touch(), 30_000);
      setTimeout(() => setStatus((s) => (s.startsWith('Equipped ') ? '' : s)), 4000);
    } catch (e) {
      setStatus(`${item.name} was not equipped. ${whyNot(e)}`);
    } finally { setBusy(false); }
  };

  const square = (it: WornItem, size: number, region: string | null, isArmour: boolean) => (
    <div key={it.bucket} className={`g3d-sq ${isArmour ? 'armour' : ''} ${it.masterwork ? 'mwork' : ''}`} style={{ '--sz': `${size}px`, '--tier': TIER_COLORS[it.tier] ?? '#5a6170' } as CSSProperties}
      onPointerEnter={() => { setHover(it); window.clearTimeout(focusWait.current); if (region !== null) focusWait.current = window.setTimeout(() => run('focusOn', region), FOCUS_WAIT); }}
      onPointerLeave={() => { setHover(undefined); window.clearTimeout(focusWait.current); if (region !== null) run('focusOn', ''); }}
      onClick={(e) => { setSubOpen(undefined); setOpen({ item: it, armour: isArmour, at: e.currentTarget.getBoundingClientRect() }); }}>
      <Pic src={it.icon} />
      {it.watermark && <Pic src={it.watermark} />}
      {it.gearTier > 0 && <span className="pips">{Array.from({ length: Math.min(5, it.gearTier) }, (_, i) => <i key={i} />)}</span>}
      {it.masterwork && <span className="mw" />}
      {it.power !== undefined && <span className="g3d-sqpower">{it.power}</span>}
      {/* the game's little grid beside a slot: one square for each of the nine other items it can carry, lit when held */}
      {size >= 100 && stash && char && (
        <span className={`g3d-slotgrid ${isArmour ? 'right' : 'left'}`} aria-hidden="true">
          {Array.from({ length: 9 }, (_, k) => <i key={k} className={k < inBucket(stash, it.bucket, char.id).length ? 'on' : ''} />)}
        </span>
      )}
      <div className={`g3d-card ${isArmour ? 'left' : ''}`}>
        <div className="bar" style={{ background: TIER_COLORS[it.tier] ?? '#5a6170' }}><b>{it.name.toUpperCase()}</b><span>{it.type}</span></div>
        {it.shot && <img src={img(it.shot)} alt="" />}
        <div className="body">{it.power !== undefined && <b className="gold">Power {it.power}</b>}{it.flavor && <i>{it.flavor}</i>}</div>
      </div>
    </div>
  );

  if (!char) return <div className="g3d-stage"><p className="g3d-centre">No character to show.</p></div>;
  const weapons = me ? WEAPON_BUCKETS.map((b) => meItems.find((i) => i.bucket === b)).filter((i): i is WornItem => !!i) : [];
  const extras = me ? EXTRA_BUCKETS.map((b) => meItems.find((i) => i.bucket === b)).filter((i): i is WornItem => !!i) : [];
  const held = open && stash ? inBucket(stash, open.item.bucket, char.id).sort((a, b) => (b.power ?? 0) - (a.power ?? 0)).slice(0, 9) : [];
  const hours = Math.round(char.minutes / 60);
  const mapUrl = placeNow ? `&map=${encodeURIComponent(placeNow.url)}` : '';

  return (
    <div className={`g3d-stage ${calm ? 'calm' : ''} ${open || subOpen ? 'popping' : ''}`} ref={stage} style={{ '--k': k, background: `radial-gradient(70% 80% at 50% 55%, ${dark(colour, 0.36)}, ${dark(colour, 0.1)} 70%, ${dark(colour, 0)})` } as CSSProperties}
      onClick={(e) => { if (!(e.target as HTMLElement).closest('.g3d-sq, .g3d-pop, .g3d-diamond, .g3d-more')) { setOpen(undefined); setSubOpen(undefined); setMore(false); } }}>
      {scene && <iframe ref={frame} key={frameKey} title="Your Guardian in 3D" onLoad={() => { lineUp(); frameScene.current = shownKey; }}
        src={`/viewer3d.html?scene=mem&stage=1&dark=1&tint=%23${colour.slice(1)}&bg=%23101216,%230B0C10,%23050608${mapUrl}&t=${encodeURIComponent(frameKey)}`} />}

      {status && <div className={`g3d-toast ${/not equipped/.test(status) ? 'bad' : busy ? '' : 'good'}`} role="status" onClick={() => !busy && setStatus('')}>{status}</div>}
      <div className="g3d-over">
        <div className="g3d-top">
          <div className="g3d-title">
            <span className="accent" style={{ background: colour }} />
            <div><div className="name">{char.cls.toUpperCase()}</div><div className="sub">{[char.race, me?.title, sub?.name].filter(Boolean).join('   //   ').toUpperCase()}</div></div>
          </div>
          <div className="g3d-chars">
            {h.hub?.chars.map((c) => (
              <button key={c.id} type="button" className="g3d-char" style={{ opacity: c.id === char.id ? 1 : 0.55 }} onClick={() => { setOpen(undefined); h.setChar(c.id); }}>
                <b>{c.cls}</b><span className="gold">{c.light}</span>
              </button>
            ))}
            <button type="button" className="g-small" onClick={() => h.refresh()}>Refresh</button>
          </div>
        </div>


        <div className="g3d-body" ref={body}>
          <div className="g3d-left" ref={left}>
            {sub && (
              <button type="button" className={`g3d-diamond ${/prismatic/i.test(sub.name) ? 'round' : ''}`} style={{ background: colour }} title={`${sub.name}  -  click to see super, abilities, aspects and fragments`}
                onClick={(e) => { setOpen(undefined); setSubHover(undefined); setSubOpen(subOpen ? undefined : e.currentTarget.getBoundingClientRect()); }}>
                <Pic src={sub.icon} />
              </button>
            )}
            {sub && (() => {
              // as in the game: the Super as a small diamond beside the subclass, the abilities in a column by the weapons
              const sup = sub.plugs.find((p) => p.kind === 'Super');
              const abilities = sub.plugs.filter((p) => p.kind === 'Ability').slice(0, 4);
              return (
                <>
                  {sup && <span className="g3d-supermark" title={`${sup.name} (Super)`}><Pic src={sup.icon} /></span>}
                  {abilities.length > 0 && <div className="g3d-abilities">{abilities.map((a) => <span key={a.name} title={`${a.name}\n${a.type}`}><Pic src={a.icon} /></span>)}</div>}
                </>
              );
            })()}
            {weapons.map((w) => square(w, 108, null, false))}
            {extras.length > 0 && <div className="g3d-extras">{extras.map((x) => square(x, 68, null, false))}</div>}
          </div>
          <div className="g3d-mid">
            {centre && <p className="g3d-centre">{centre}</p>}
            {!scene && !centre && <div className="g3d-wait"><Art d={Draw.Cube} size={90} stroke={0.4} /><p>{error ?? 'Getting ready...'}</p></div>}
            {me?.title && <div className="g3d-titlebar" style={{ ['--glow' as string]: colour }}><span>{me.title.toUpperCase()}</span></div>}
            <div className="g3d-caption">
              <div className="kind">{hover ? `${hover.power !== undefined ? `${hover.type}  //  ${hover.power}` : hover.type}  //  CLICK TO SWAP`.toUpperCase() : placeNow ? 'LOCATION' : ''}</div>
              <div className="what">{(hover ? hover.name : placeNow?.name ?? '').toUpperCase()}</div>
              <div className="rule" />
            </div>
          </div>
          <div className="g3d-right" ref={right}>
            <div className="g3d-power"><div>POWER</div><b>{char.light}</b></div>
            <div className="g3d-statsarmour">
              {me && me.stats.length > 0 && (
                <div className="g3d-stats">
                  {(h.hub?.guardianRank ?? 0) > 0 && <div className="g3d-hex" title={`Guardian Rank ${h.hub!.guardianRank}`}><b>{h.hub!.guardianRank}</b></div>}
                  {me.stats.map((s) => (
                    <div key={s.name} className="row" title={`${s.name} ${s.value}`}>
                      <Pic src={s.icon} width={20} height={20} />
                      <span>{s.name.toUpperCase()}</span>
                      <i><i style={{ width: `${Math.min(1, s.value / 200) * 100}%`, background: `linear-gradient(90deg, ${dark(colour, 0.7)}, ${colour})`, boxShadow: `0 0 8px ${colour}` }} /></i>
                      <b>{s.value}</b>
                    </div>
                  ))}
                  <div className="total"><span>TOTAL</span><b>{me.stats.reduce((n, s) => n + s.value, 0)}</b></div>
                </div>
              )}
              <div className="g3d-armour">{armour.map((a, n) => square(a, 108, REGIONS[Math.min(n, REGIONS.length - 1)], true))}</div>
            </div>
          </div>
        </div>

        <div className="g3d-foot">
          <div className="facts">
            <span>{hours.toLocaleString()} hours played   |   last played {char.lastPlayed.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
            {info && <span className="dim">{info}</span>}
          </div>
          <div className="tools">
            <button type="button" className="g-small" title="Stands your Guardian somewhere else in the game" onClick={() => {
              if (!places.length) return;
              const next = (placeAt + 1) % (places.length + 1);
              setPlace(next);
              store.set(PLACE_KEY, next < places.length ? places[next].name : '');
              run('setMap', next < places.length ? places[next].url : '');
            }}>Change place</button>
            <button type="button" className="g-small" title="Turns the Guardian slowly by itself" onClick={() => run('toggleSpin')}>Turntable</button>
            <button type="button" className="g-small" title="Back to the first view (a double-click on the Guardian does the same)" onClick={() => run('resetView')}>Reset view</button>
            <button type="button" className="g-small" title="Opens your items and vault (Character > Items)" onClick={() => nav('/guardian/character')}>Items</button>
            <span className="g3d-more">
              <button type="button" className="g-small" title="Material views and fixes for a model that looks wrong" onClick={() => setMore(!more)}>More</button>
              {more && (
                <div className="g3d-menu">
                  <button type="button" onClick={() => { const v = (view + 1) % VIEWS.length; setView(v); run('setView', VIEWS[v][0]); }}>View: {VIEWS[view][1]}</button>
                  <button type="button" onClick={() => run('flipUp')}>Flip upright</button>
                  <button type="button" onClick={() => run('flipTextures')}>Flip textures</button>
                  <button type="button" onClick={() => run('flipNormalY')}>Flip normal map (green)</button>
                  <button type="button" onClick={() => { scenes.delete(key); setMore(false); setShownKey(''); setScene(undefined); h.touch(); }}>Rebuild 3D from Bungie's gear data</button>
                </div>
              )}
            </span>
          </div>
        </div>
      </div>

      {open && (
        <div className="g3d-pop" style={{ borderColor: colour, top: Math.min(open.at.top - (stage.current?.getBoundingClientRect().top ?? 0), (stage.current?.clientHeight ?? 600) - 340),
          ...(open.armour ? { right: (stage.current?.getBoundingClientRect().right ?? 0) - open.at.left + 10 } : { left: open.at.right - (stage.current?.getBoundingClientRect().left ?? 0) + 10 }) }}>
          <div className="head">{open.item.type.toUpperCase()}</div>
          {!h.own ? <p>Sign in with Bungie (top right) to see what this slot carries and swap it.</p>
            : !stash ? <p>Reading what this slot carries...</p>
              : held.length === 0 ? <p>Nothing else is carried in this slot. What is in your Vault is on the Vault page (button at the bottom right).</p>
                : (
                  <>
                    <p className="dim">Wearing {open.item.name}. Click another to equip it.</p>
                    <div className="grid">
                      {held.map((it) => (
                        <button key={it.instanceId || it.hash} type="button" disabled={busy} className={`pick ${isMasterwork(it) ? 'mwork' : ''}`} style={{ background: TIER_COLORS[it.tier] ?? '#5a6170' }}
                          title={`${it.name}\n${it.typeName}${it.power ? `  |  Power ${it.power}` : ''}\nClick to equip`} onClick={() => void equip(it, open.armour)}>
                          <Pic src={it.icon} />
                          {it.power !== undefined && <span className="gold">{it.power}</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
        </div>
      )}

      {subOpen && sub && (() => {
        // the game's subclass screen, small: the name large, the Super as a diamond, then the abilities, aspects and
        // fragments in rows under thin rules, and a card for the one under the pointer (the Super until then)
        const plugsOf = (kind: string) => sub.plugs.filter((p) => p.kind === kind);
        const sup = plugsOf('Super')[0];
        const shown = subHover ?? sup;
        const slot = (p: (typeof sub.plugs)[number], k: number, cls = '') => (
          <span key={k} className={`g3d-subslot ${cls} ${shown === p ? 'on' : ''}`} onPointerEnter={() => setSubHover(p)} title={p.name}><Pic src={p.icon} /></span>
        );
        const row = (title: string, kind: string) => {
          const list = plugsOf(kind);
          return list.length ? (
            <div className="g3d-subrow" key={kind}>
              <div className="h">{title}</div>
              <div className="slots">{list.map((p, k) => slot(p, k, kind === 'Fragment' ? 'small' : ''))}</div>
            </div>
          ) : null;
        };
        return (
          <div className="g3d-pop sub g3d-subscreen" style={{ borderColor: colour, ['--sc' as string]: colour, top: subOpen.top - (stage.current?.getBoundingClientRect().top ?? 0), left: subOpen.right - (stage.current?.getBoundingClientRect().left ?? 0) + 14 }}>
            <div className="g3d-subhead">
              <span className="icon" style={{ background: colour }}><Pic src={sub.icon} /></span>
              <div><b>{sub.name.toUpperCase()}</b><small>{(sub.type || `${char.cls} Subclass`).toUpperCase()}</small></div>
            </div>
            {sub.plugs.length === 0 ? <span className="dim">No subclass details available.</span> : (
              <div className="g3d-subbody">
                {sup && (
                  <div className="g3d-subsuper" onPointerEnter={() => setSubHover(sup)}>
                    <span className="d" style={{ background: colour }}><Pic src={sup.icon} /></span>
                    <span className="n">{sup.name}</span>
                  </div>
                )}
                <div className="g3d-subrows">
                  <div className="g3d-subpair">{row('Abilities', 'Ability')}{row('Aspects', 'Aspect')}</div>
                  {row('Fragments', 'Fragment')}
                </div>
                {shown && (
                  <div className="g3d-subcard">
                    <div className="bar" style={{ background: colour }}><b>{shown.name.toUpperCase()}</b><span>{shown.type}</span></div>
                    {shown.desc && <p>{shown.desc}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
